import React, { useEffect, useMemo, useState } from "react";
import {
  X, Plus, Trash2, Send, Save, MessageSquareReply, Link as LinkIcon, Phone,
} from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";

const EMPTY_TEMPLATE = {
  id: "",
  name: "",
  category: "MARKETING",
  language: "pt_BR",
  header_type: "NONE",
  header_text: "",
  body: "",
  footer: "",
  buttons: [],
  status: "DRAFT",
};

function createButton(type) {
  return {
    id: crypto.randomUUID(),
    type,
    text: "",
    url: "",
    phone_number: "",
  };
}

function PhonePreview({ form }) {
  const previewButtons = form.buttons || [];
  return (
    <div className="mx-auto h-[clamp(455px,60vh,500px)] max-h-full aspect-[9/18.2] w-auto max-w-full rounded-[2.4rem] border-[9px] border-slate-950 bg-[#0f1f2f] shadow-[0_28px_80px_rgba(0,0,0,0.5)]">
      <div className="mx-auto mt-1.5 h-1.5 w-24 rounded-full bg-slate-700" />
      <div className="relative mt-2 flex h-[calc(100%-1.25rem)] flex-col overflow-hidden rounded-[1.7rem] bg-[radial-gradient(circle_at_top,_rgba(37,211,102,0.10),_transparent_38%),linear-gradient(180deg,#102437,#0d1b29)] px-3 pb-3.5 pt-3.5">
        <div className="mb-4 flex items-center justify-between text-[10px] text-slate-400">
          <span>WhatsApp Business</span>
          <span>Preview</span>
        </div>
        <div className="flex flex-1 items-start justify-end overflow-y-auto pr-1">
          <div className="ml-auto max-w-[90%] rounded-2xl rounded-tr-md bg-[#1f3d35] px-3 py-2.5 text-sm text-slate-100 shadow-lg">
            {form.header_type === "TEXT" && form.header_text ? (
              <div className="mb-2 text-[12px] font-semibold leading-5 text-[#9de9b4]">{form.header_text}</div>
            ) : null}
            <div className="whitespace-pre-wrap break-words text-[12px] leading-5">
              {form.body || "Digite o corpo da mensagem para visualizar aqui..."}
            </div>
            {form.footer ? (
              <div className="mt-3 border-t border-white/10 pt-2 text-[11px] leading-4 text-slate-300/80">{form.footer}</div>
            ) : null}
            {previewButtons.length > 0 ? (
              <div className="mt-3 space-y-2 border-t border-white/10 pt-3">
                {previewButtons.map((button) => (
                  <div key={button.id} className="rounded-2xl bg-white/5 px-3 py-2 text-center text-[11px] font-medium text-[#7fe0f7]">
                    {button.text || (button.type === "quick_reply" ? "Resposta rápida" : button.type === "url" ? "Abrir link" : "Ligar agora")}
                  </div>
                ))}
              </div>
            ) : null}
            <div className="mt-3 text-right text-[10px] text-slate-400">21:55</div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ModalBuilder({
  open,
  template,
  busy,
  onClose,
  onSaveDraft,
  onSubmitTemplate,
}) {
  const [form, setForm] = useState(EMPTY_TEMPLATE);

  useEffect(() => {
    if (!open) return;
    setForm({
      ...EMPTY_TEMPLATE,
      ...(template || {}),
      id: template?.id || crypto.randomUUID(),
      buttons: Array.isArray(template?.buttons) ? template.buttons : [],
    });
  }, [open, template]);

  const quickReplyCount = useMemo(
    () => (form.buttons || []).filter((button) => button.type === "quick_reply").length,
    [form.buttons]
  );
  const ctaCount = useMemo(
    () => (form.buttons || []).filter((button) => button.type === "url" || button.type === "phone").length,
    [form.buttons]
  );

  const canSubmit = form.name.trim() && form.category && form.body.trim();
  const canAddQuickReply = ctaCount === 0 && quickReplyCount < 3;
  const canAddCta = quickReplyCount === 0 && ctaCount < 2;

  if (!open) return null;

  const updateButton = (id, patch) => {
    setForm((current) => ({
      ...current,
      buttons: current.buttons.map((button) => (button.id === id ? { ...button, ...patch } : button)),
    }));
  };

  const removeButton = (id) => {
    setForm((current) => ({
      ...current,
      buttons: current.buttons.filter((button) => button.id !== id),
    }));
  };

  const addButton = (type) => {
    setForm((current) => ({
      ...current,
      buttons: [...current.buttons, createButton(type)],
    }));
  };

  const handleSave = () => onSaveDraft?.(form);
  const handleSubmit = () => onSubmitTemplate?.(form);

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/60 px-4 py-8 backdrop-blur-sm">
      <div className="flex max-h-[92vh] w-full max-w-6xl flex-col overflow-hidden rounded-3xl border border-slate-700 bg-[#12263a] shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-700 px-6 py-4">
          <div>
            <div className="mb-1 text-[10px] uppercase tracking-[0.25em] text-slate-400">Modelos Oficiais do WhatsApp</div>
            <h2 className="text-xl font-bold text-slate-50" style={{ fontFamily: "Outfit" }}>
              {template?.id ? "Editar template oficial" : "Novo template oficial"}
            </h2>
          </div>
          <div className="flex items-center gap-2">
            <Button type="button" variant="ghost" onClick={onClose} className="text-slate-300 hover:bg-slate-800 hover:text-slate-50">
              <X className="mr-1.5 h-4 w-4" /> Fechar
            </Button>
          </div>
        </div>

        <div className="grid min-h-0 flex-1 grid-cols-1 gap-0 lg:grid-cols-[minmax(0,1.28fr)_minmax(300px,0.72fr)]">
          <div className="min-h-0 overflow-y-auto border-r border-slate-800 px-6 py-5">
            <div className="mb-5 flex flex-wrap items-center gap-3">
              <span className="rounded-full border border-slate-700 bg-slate-900 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-slate-300">
                {form.status === "DRAFT" ? "Rascunho" : form.status}
              </span>
              <span className="text-xs text-slate-400">Preencha os campos e envie para análise da Meta.</span>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-slate-400">Categoria</Label>
                <select
                  value={form.category}
                  onChange={(e) => setForm((current) => ({ ...current, category: e.target.value }))}
                  className="mt-1 h-10 w-full rounded-md border border-slate-700 bg-slate-900 px-3 text-sm text-slate-100 outline-none"
                >
                  <option value="MARKETING">Marketing</option>
                  <option value="UTILITY">Utilidade</option>
                </select>
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-slate-400">Idioma</Label>
                <select
                  value={form.language}
                  onChange={(e) => setForm((current) => ({ ...current, language: e.target.value }))}
                  className="mt-1 h-10 w-full rounded-md border border-slate-700 bg-slate-900 px-3 text-sm text-slate-100 outline-none"
                >
                  <option value="pt_BR">Português (Brasil)</option>
                  <option value="en_US">Inglês (EUA)</option>
                  <option value="es_ES">Espanhol</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <Label className="text-[11px] uppercase tracking-wider text-slate-400">Título do template</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm((current) => ({ ...current, name: e.target.value.toLowerCase().replace(/\s+/g, "_") }))}
                  placeholder="ex: oferta_relampago_abril"
                  className="mt-1 border-slate-700 bg-slate-900 text-slate-100"
                />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-slate-400">Cabeçalho</Label>
                <select
                  value={form.header_type}
                  onChange={(e) => setForm((current) => ({ ...current, header_type: e.target.value, header_text: e.target.value === "NONE" ? "" : current.header_text }))}
                  className="mt-1 h-10 w-full rounded-md border border-slate-700 bg-slate-900 px-3 text-sm text-slate-100 outline-none"
                >
                  <option value="NONE">Sem cabeçalho</option>
                  <option value="TEXT">Texto</option>
                </select>
              </div>
              {form.header_type === "TEXT" ? (
                <div>
                  <Label className="text-[11px] uppercase tracking-wider text-slate-400">Texto do cabeçalho</Label>
                  <Input
                    value={form.header_text}
                    onChange={(e) => setForm((current) => ({ ...current, header_text: e.target.value }))}
                    placeholder="Ex: Oferta exclusiva"
                    className="mt-1 border-slate-700 bg-slate-900 text-slate-100"
                  />
                </div>
              ) : null}
              <div className="md:col-span-2">
                <Label className="text-[11px] uppercase tracking-wider text-slate-400">Corpo do texto</Label>
                <textarea
                  value={form.body}
                  onChange={(e) => setForm((current) => ({ ...current, body: e.target.value }))}
                  placeholder="Digite o corpo da mensagem aqui. Use {{1}} para variáveis."
                  className="mt-1 min-h-[150px] w-full rounded-xl border border-slate-700 bg-slate-900 px-3 py-3 text-sm text-slate-100 outline-none"
                />
              </div>
              <div className="md:col-span-2">
                <Label className="text-[11px] uppercase tracking-wider text-slate-400">Rodapé</Label>
                <Input
                  value={form.footer}
                  onChange={(e) => setForm((current) => ({ ...current, footer: e.target.value }))}
                  placeholder="Mensagem opcional no rodapé"
                  className="mt-1 border-slate-700 bg-slate-900 text-slate-100"
                />
              </div>
            </div>

            <div className="mt-6 rounded-2xl border border-slate-800 bg-slate-950/60 p-4">
              <div className="mb-3 flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-semibold text-slate-100">Botões</h3>
                  <p className="text-xs text-slate-400">Resposta rápida ou chamada para ação, conforme as regras da Meta.</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button type="button" variant="outline" size="sm" disabled={!canAddQuickReply} onClick={() => addButton("quick_reply")} className="border-slate-700 bg-slate-900 text-slate-100">
                    <MessageSquareReply className="mr-1.5 h-4 w-4" /> Resposta rápida
                  </Button>
                  <Button type="button" variant="outline" size="sm" disabled={!canAddCta} onClick={() => addButton("url")} className="border-slate-700 bg-slate-900 text-slate-100">
                    <LinkIcon className="mr-1.5 h-4 w-4" /> Link
                  </Button>
                  <Button type="button" variant="outline" size="sm" disabled={!canAddCta} onClick={() => addButton("phone")} className="border-slate-700 bg-slate-900 text-slate-100">
                    <Phone className="mr-1.5 h-4 w-4" /> Telefone
                  </Button>
                </div>
              </div>

              <div className="space-y-3">
                {(form.buttons || []).length === 0 ? (
                  <div className="rounded-xl border border-dashed border-slate-700 px-4 py-5 text-center text-xs text-slate-500">
                    Nenhum botão adicionado ainda.
                  </div>
                ) : (
                  form.buttons.map((button, index) => (
                    <div key={button.id} className="grid grid-cols-1 gap-3 rounded-xl border border-slate-800 bg-slate-900 px-3 py-3 md:grid-cols-[140px_1fr_1fr_auto]">
                      <div>
                        <Label className="text-[11px] uppercase tracking-wider text-slate-400">Tipo</Label>
                        <div className="mt-1 rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-200">
                          {button.type === "quick_reply" ? "Resposta rápida" : button.type === "url" ? "Link" : "Telefone"}
                        </div>
                      </div>
                      <div>
                        <Label className="text-[11px] uppercase tracking-wider text-slate-400">Texto do botão</Label>
                        <Input
                          value={button.text}
                          onChange={(e) => updateButton(button.id, { text: e.target.value })}
                          placeholder={`Botão ${index + 1}`}
                          className="mt-1 border-slate-700 bg-slate-950 text-slate-100"
                        />
                      </div>
                      {button.type === "url" ? (
                        <div>
                          <Label className="text-[11px] uppercase tracking-wider text-slate-400">URL</Label>
                          <Input
                            value={button.url}
                            onChange={(e) => updateButton(button.id, { url: e.target.value })}
                            placeholder="https://seusite.com/oferta"
                            className="mt-1 border-slate-700 bg-slate-950 text-slate-100"
                          />
                        </div>
                      ) : button.type === "phone" ? (
                        <div>
                          <Label className="text-[11px] uppercase tracking-wider text-slate-400">Telefone</Label>
                          <Input
                            value={button.phone_number}
                            onChange={(e) => updateButton(button.id, { phone_number: e.target.value })}
                            placeholder="+5511999999999"
                            className="mt-1 border-slate-700 bg-slate-950 text-slate-100"
                          />
                        </div>
                      ) : (
                        <div className="flex items-end text-xs text-slate-500">
                          O cliente toca e responde no próprio WhatsApp.
                        </div>
                      )}
                      <div className="flex items-end justify-end">
                        <Button type="button" variant="ghost" size="icon" onClick={() => removeButton(button.id)} className="text-slate-400 hover:bg-slate-800 hover:text-red-400">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          <div className="flex min-h-0 flex-col justify-between overflow-hidden bg-[linear-gradient(180deg,#102132,#0b1723)] px-5 py-5 sm:px-6">
            <div className="flex min-h-0 flex-1 flex-col items-center justify-start overflow-hidden pb-2 pt-1">
              <PhonePreview form={form} />
            </div>

            <div className="mt-3 shrink-0 rounded-2xl border border-slate-800 bg-slate-950/60 p-4">
              <div className="mb-3 text-xs text-slate-400">
                O botão Enviar só é liberado quando nome, categoria e corpo estiverem preenchidos.
              </div>
              <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
                <Button type="button" variant="outline" onClick={handleSave} disabled={busy} className="border-slate-700 bg-slate-900 text-slate-100">
                  <Save className="mr-1.5 h-4 w-4" /> Salvar rascunho
                </Button>
                <Button type="button" onClick={handleSubmit} disabled={busy || !canSubmit} className="bg-[#25D366] text-slate-950 hover:bg-[#1EAC52]">
                  <Send className="mr-1.5 h-4 w-4" /> Enviar para análise
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
